<script setup lang="ts">
import * as yup from "yup"
import { useOperationStore } from "~/stores/operation.store"
import { cloneDeep } from "lodash-es"
import type { components } from "@/assets/api/openapi"
import { IffRequiredDocuments } from "#components"

const api = useAppApi()
const { startLoading, stopLoading } = useLoading()
const isBlockedApplicant = ref<boolean>(false)
const operationStore = useOperationStore()
const medicalCouncils = ref<components["schemas"]["MedicalCouncil"][]>([])

const medicalCouncilsOptions = computed<
  components["schemas"]["MedicalCouncil"][]
>(() =>
  medicalCouncils.value.filter(
    (medicalCouncil: components["schemas"]["MedicalCouncil"]) =>
      medicalCouncil.idProfession ===
      operationAdditionalDataAhStore.data?.professionId
  )
)

const operationAdditionalDataAhStore = useOperationAdditionalDataAhStore()

const schema = yup.object().shape({
  shipped: yup
    .boolean()
    .test(
      "at-least-one-required",
      'Es muss entweder "Versand bestätigt" oder "Eingangsbestätigung nicht erforderlich" ausgewählt werden',
      function (value) {
        const isUpdateMode = this.options.context?.mode === "update"
        if (isUpdateMode) return true

        const { noConfirmationRequired } = this.parent
        return value === true || noConfirmationRequired === true
      }
    ),

  noConfirmationRequired: yup
    .boolean()
    .test(
      "at-least-one-required",
      'Es muss entweder "Versand bestätigt" oder "Eingangsbestätigung nicht erforderlich" ausgewählt werden',
      function (value) {
        const isUpdateMode = this.options.context?.mode === "update"
        if (isUpdateMode) return true

        const { shipped } = this.parent
        return value === true || shipped === true
      }
    ),
  shippingConfirmationDate: yup.string().when("shipped", {
    is: true,
    then: (s) => s.required("Pflichtfeld"),
    otherwise: (s) => s.strip(),
  }),

  council: yup
    .object({
      id: yup.string(),
      councilName: yup.string(),
    })
    .optional()
    .test("is-required", "Pflichtfeld", function (value: any) {
      const isUpdateMode = this.options.context?.mode === "update"
      if (isUpdateMode) {
        return true
      }
      return (
        value !== null &&
        value.id !== undefined &&
        value.councilName !== undefined
      )
    }),
})
type Schema = yup.InferType<typeof schema>

const currentWorkStepData = computed<Schema>(
  () => (operationStore.currentWorkStep?.data ?? {}) as unknown as Schema
)
const state = reactive<Schema>(cloneDeep(currentWorkStepData.value))

const getMedicalCouncils = async () => {
  startLoading()
  try {
    const { data, status } = await api.getMedicalCouncils()
    if (status === 200 && data) {
      medicalCouncils.value = data
    } else {
      showError({
        statusCode: status,
      })
    }
  } catch {
    showError({
      statusCode: 500,
    })
  } finally {
    stopLoading()
  }
}

const checkBlockedApplicant = async () => {
  if (operationStore.operation?.id) {
    try {
      const { data, status } = await api.checkBlockedApplicant(
        operationStore.operation.id
      )
      if (status === 200 && data) {
        isBlockedApplicant.value = !!data.blockedApplicant
        startLoading()
      } else {
        showToast("warning", {
          title: "Achtung",
          description: "Die Warndatei-Prüfung konnte nicht vorgenommen werden.",
        })
      }
    } catch {
      showToast("warning", {
        title: "Achtung",
        description: "Die Warndatei-Prüfung konnte nicht vorgenommen werden.",
      })
    } finally {
      stopLoading()
    }
  }
}

const setMedicalCouncils = async () => {
  await getMedicalCouncils()
  if (medicalCouncils.value?.length === 1 && !state.council) {
    state.council = medicalCouncils.value[0]
    operationStore.unsavedChanges = false
  }
}

const initializeOperationAdditionalDataAhStore = async () => {
  if (operationStore.operation?.id) {
    await operationAdditionalDataAhStore.fetchIfNeeded(
      operationStore.operation.id
    )
  }
}

watch(
  () => state.shipped,
  (value) => {
    if (value === true) {
      state.noConfirmationRequired = undefined
    }
    if (value === false) {
      state.shippingConfirmationDate = undefined
    }
  }
)

watch(
  () => state.noConfirmationRequired,
  (value) => {
    if (value) {
      state.shipped = undefined
      state.shippingConfirmationDate = undefined
    }
  }
)

onMounted(() => {
  initializeOperationAdditionalDataAhStore()
  checkBlockedApplicant()
  setMedicalCouncils()
})
</script>

<template>
  <IffOperationWorkStepFormV2 :state="state" :schema="schema">
    <template #above-form="{ disabled }">
      <IffMessage
        v-if="isBlockedApplicant"
        variant="warning"
        class="w-[600px] mb-10"
        :class="{ 'opacity-50': disabled }"
      >
        <IffCopyText>
          Es existiert bereits ein Datensatz mit vergleichbaren Daten in der
          Warndatei. Bitte überprüfen Sie, ob es sich um die genannte Person
          handelt.
        </IffCopyText>
      </IffMessage>

      <IffReviewsSection
        v-if="operationStore.currentWorkStep"
        :work-step-id="operationStore.currentWorkStep?.id"
        additional-headline="Eingangsbestätigung"
      />
    </template>

    <IffContentSection headline="Kammer zuordnen">
      <UFormGroup
        class="w-[600px]"
        name="council"
        label="Zuständige Kammer"
        required
        :ui="{ wrapper: 'mb-0' }"
      >
        <USelectMenu
          v-model="state.council"
          placeholder="Bitte wählen"
          required
          :options="medicalCouncilsOptions"
          option-attribute="councilName"
          :ui="{
            icon: {
              trailing: { pointer: '' },
              base: state.council !== undefined ? 'pr-12' : '',
            },
          }"
        >
          <template #trailing v-if="state.council !== undefined">
            <UButton
              color="gray"
              variant="link"
              icon="i-ic-close"
              :padded="false"
              aria-label="Zuständige Kammer löschen"
              @click.stop="state.council = undefined"
            />
          </template>
        </USelectMenu>
      </UFormGroup>
    </IffContentSection>

    <IffContentSection headline="Nachweise prüfen">
      <UButton
        variant="outline"
        @click="() => operationStore.changeMainView(IffRequiredDocuments)"
      >
        Nachweisliste öffnen
      </UButton>
    </IffContentSection>

    <IffContentSection
      headline="Versand Eingangsbestätigung"
      :is-last-child="true"
    >
      <UFormGroup name="shipped">
        <UCheckbox
          label="Der Versand der erforderlichen Dokumente an die antragstellende Person ist erfolgt."
          v-model="state.shipped"
          :required="!state.noConfirmationRequired"
          :aria-required="!state.noConfirmationRequired"
          aria-controls="shipping-confirmation-date-region"
        />
      </UFormGroup>
      <div id="shipping-confirmation-date-region" aria-live="polite">
        <IffTransitionHeight>
          <UFormGroup
            name="shippingConfirmationDate"
            label="Datum Eingangsbestätigung"
            v-if="state.shipped"
            required
            class="max-w-[270px]"
          >
            <template #hint>
              <IffIconTooltip
                text="BQFG: Wert wird in Datum der Empfangsbestätigung (e01) übernommen."
              />

              <UButton
                @click="
                  state.shippingConfirmationDate = new Date()
                    .toISOString()
                    .slice(0, 10)
                "
                icon="i-ic-outline-access-time"
                size="sm"
                color="gray"
                variant="ghost"
                :trailing="false"
                aria-label="Aktuelles Datum wählen"
                title="Aktuelles Datum wählen"
              />
            </template>

            <UInput
              v-model="state.shippingConfirmationDate"
              type="date"
              aria-required="true"
              class="whitespace-normal max-w-[150px]"
            />
          </UFormGroup>
        </IffTransitionHeight>
      </div>
      <UFormGroup name="noConfirmationRequired">
        <UCheckbox
          label="Eingangsbestätigung nicht erforderlich."
          v-model="state.noConfirmationRequired"
          :required="!state.shipped"
          :aria-required="!state.shipped"
        />
      </UFormGroup>
    </IffContentSection>
  </IffOperationWorkStepFormV2>
</template>
